'use client';

import { useEffect, useRef, useState } from 'react';

interface LazyVideoProps {
  src: string;
  poster?: string;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  autoPlay?: boolean;
  controls?: boolean;
  isActive?: boolean; // 외부에서 제어할 때 사용
  className?: string;
}

const LazyVideo = ({
  src,
  poster,
  muted = false,
  loop = false,
  playsInline = false,
  autoPlay = false,
  controls = true,
  isActive = true,
  className,
}: LazyVideoProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  // lazy load
  useEffect(() => {
    if (!videoRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.25 },
    );

    observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, []);

  // active 상태에 따라 재생/정지/리셋
  useEffect(() => {
    if (!videoRef.current) return;

    if (isActive) {
      videoRef.current.play().catch((err) => {
        console.warn('자동재생 실패:', err);
      });
    } else {
      videoRef.current.pause();
      videoRef.current.currentTime = 0; // 필요 시 처음으로 리셋
    }
  }, [isActive]);

  return (
    <video
      ref={videoRef}
      poster={poster}
      preload="none"
      className={className}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      autoPlay={autoPlay}
      controls={controls}
    >
      {isVisible && <source src={src} type="video/mp4" />}
    </video>
  );
};

export default LazyVideo;
